import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from '../config/baseUrl';

// Calendar Icon
const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

// Swap Right Icon
const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

// Down Icon for Select
const DownIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z"></path>
  </svg>
);

// Menu Down Icon
const MenuDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

// Left Arrow Icon
const LeftIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

// Double Right Icon
const DoubleRightIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M533.2 492.3L277.9 166.1c-3-3.9-7.7-6.1-12.6-6.1H188c-6.7 0-10.4 7.7-6.3 12.9L447.1 512 181.7 851.1A7.98 7.98 0 00188 864h77.3c4.9 0 9.6-2.3 12.6-6.1l255.3-326.1c9.1-11.7 9.1-27.9 0-39.5zm304 0L581.9 166.1c-3-3.9-7.7-6.1-12.6-6.1H492c-6.7 0-10.4 7.7-6.3 12.9L751.1 512 485.7 851.1A7.98 7.98 0 00492 864h77.3c4.9 0 9.6-2.3 12.6-6.1l255.3-326.1c9.1-11.7 9.1-27.9 0-39.5z"></path>
  </svg>
);

// Game Types
const GAME_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'cricket', label: 'Cricket' },
];

// Fetch completed matches
const fetchCompletedMatches = async function (index, noOfRecords) {
  try {
    const token = getAccessToken();

    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/sports/get-completed-events",
      { index: index, noOfRecords: noOfRecords },
      { headers: { Authorization: "Bearer " + token } },
    );

    const data = response.data;

    if (data.status === false) {
      return {
        success: false,
        error: data.message || "Failed to fetch matches",
      };
    }

    return {
      success: true,
      data: data.data.completedMatchList || [],
      totalPages: data.data.totalPages || 0,
      currentPage: data.data.currentPage || 0,
    };
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || "Failed to fetch matches";
    return { success: false, error: errorMessage };
  }
};

// Game Type Dropdown Component
const GameTypeDropdown = ({ value, onChange, onClose }) => {
  const dropdownRef = useRef(null);

  useEffect(function() {
    const handleClickOutside = function(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return function() {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 bg-white border border-gray-300 shadow-lg z-50 mt-1"
      style={{ borderRadius: 0 }}
    >
      {GAME_TYPES.map(function(type) {
        const isSelected = value === type.value;
        return (
          <div
            key={type.value}
            onClick={function() {
              onChange(type.value);
              onClose();
            }}
            className={
              'px-3 py-2 cursor-pointer hover:bg-gray-100 ' +
              (isSelected ? 'bg-blue-50 text-blue-600' : '')
            }
          >
            {type.label}
          </div>
        );
      })}
    </div>
  );
};

// Dropdown Menu Component
const DropdownMenu = ({ match, position, onClose }) => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const matchId = match.matchId || match.id;
  const rawMatchName = match.matchName;
  const matchName =
    rawMatchName && rawMatchName !== "undefined" && rawMatchName !== "null"
      ? rawMatchName
      : match.eventName || match.name || String(matchId || "");

  useEffect(
    function () {
      const handleClickOutside = function (event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return function () {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    },
    [onClose],
  );

  const handleMatchPosition = function (e) {
    e.preventDefault();
    navigate("/dashboard/match/" + matchId);
  };

  const menuItems = [
    {
      key: "plusminus",
      label: "Match and Session Plus Minus",
      onClick: function () {
        navigate(
          "/sports-betting/match-session-plus-minus/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "plusminus2",
      label: "Match and Session Plus Minus 2",
      onClick: function () {
        navigate(
          "/sports-betting/company-report/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "matchbets",
      label: "Display Match Bets",
      onClick: function () {
        navigate(
          "/sports-betting/match-bets/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
          { state: { matchCompleted: true } }
        );
        onClose();
      },
    },
    {
      key: "tossbets",
      label: "Display Toss Bets",
      onClick: function () {
        navigate(
          "/sports-betting/toss-bets/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
          { state: { matchCompleted: true } }
        );
        onClose();
      },
    },
    {
      key: "sessionbets",
      label: "Display Session Bets",
      onClick: function () {
        navigate(
          "/sports-betting/fancy-bets/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
          { state: { matchCompleted: true } }
        );
        onClose();
      },
    },
    {
      key: "matchsessionbet",
      label: "Match And Session Bet",
      path: "/components/matchsessionbet/match-sessionbet/" + match.marketId,
    },
    {
      key: "completed",
      label: "Completed Fancies",
      onClick: function () {
        navigate(
          "/sports-betting/fancy-profit-loss/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "rejected",
      label: "Rejected Bets",
      onClick: function () {
        navigate(
          "/sports-betting/rejected-bets/" +
            matchId +
            "/" +
            encodeURIComponent(matchName),
        );
        onClose();
      },
    },
    {
      key: "agent",
      label: "Agent Plus Minus",
      onClick: function() {
        navigate('/sports-betting/agent-plus-minus/' + matchId + '/' + encodeURIComponent(matchName));
        onClose();
      }
    },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}></div>
      <ul
        ref={menuRef}
        className="absolute bg-white rounded shadow-lg z-50 py-1 min-w-[280px] list-none m-0 p-0"
        style={{
          top: position.top,
          left: position.left,
          boxShadow:
            "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
        }}
        role="menu"
      >
        {menuItems.map(function (item) {
          return (
            <li
              key={item.key}
              role="menuitem"
              tabIndex={-1}
              onClick={
                item.onClick ||
                function () {
                  console.log("Navigate to:", item.path);
                  onClose();
                }
              }
              className="px-4 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 font-semibold text-gray-800"
              style={{ lineHeight: "22px" }}
            >
              <span>{item.label}</span>
            </li>
          );
        })}
      </ul>
    </>
  );
};

// Format date for display
const formatDisplayDate = function (dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");

  return (
    day +
    "-" +
    month +
    "-" +
    year +
    " " +
    hoursStr +
    ":" +
    minutes +
    ":" +
    seconds +
    " " +
    ampm
  );
};

// Format date for input field (YYYY-MM-DD)
const formatDateForInput = function(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
};

// Format date for display (DD-MM-YYYY)
const formatDateForDisplay = function(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return day + '-' + month + '-' + year;
};

const CompletedGamesPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedGameType, setSelectedGameType] = useState('all');
  const [showGameTypeDropdown, setShowGameTypeDropdown] = useState(false);

  const RECORDS_PER_PAGE = 20;

  // Fetch matches
  const loadMatches = async function (pageIndex) {
    setLoading(true);
    setError(null);

    const result = await fetchCompletedMatches(pageIndex, RECORDS_PER_PAGE);

    if (result.success) {
      setAllMatches(result.data);
      setFilteredMatches(result.data);
      setTotalPages(result.totalPages);
      setCurrentPage(result.currentPage);
    } else {
      setError(result.error);
      setAllMatches([]);
      setFilteredMatches([]);
    }

    setLoading(false);
  };

  useEffect(function () {
    loadMatches(0);
  }, []);

  // Apply filters
  const applyFilters = function() {
    let filtered = [...allMatches];

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter(function(match) {
        const matchDate = new Date(match.createdOn);
        matchDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
        
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        // Set time to start/end of day for proper comparison
        if (start) {
          start.setHours(0, 0, 0, 0);
        }
        if (end) {
          end.setHours(0, 0, 0, 0);
        }

        // If both dates are provided, check if match is within range
        if (start && end) {
          return matchDate >= start && matchDate <= end;
        } 
        // If only start date is provided, show matches from that date onwards
        else if (start && !end) {
          return matchDate >= start;
        } 
        // If only end date is provided, show matches up to that date
        else if (!start && end) {
          return matchDate <= end;
        }
        return true;
      });
    }

    // Filter by game type
    if (selectedGameType !== 'all') {
      filtered = filtered.filter(function(match) {
        const matchType = (match.matchtype || match.eventName || '').toLowerCase();
        return matchType.includes(selectedGameType.toLowerCase());
      });
    }

    setFilteredMatches(filtered);
  };

  // Apply filters whenever filter values change
  useEffect(function() {
    applyFilters();
  }, [startDate, endDate, selectedGameType, allMatches]);

  const handleDropdownClick = function (e, matchId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setActiveDropdown(activeDropdown === matchId ? null : matchId);
  };

  const handleCloseDropdown = function () {
    setActiveDropdown(null);
  };

  const handlePageChange = function (newPage) {
    if (newPage >= 0 && newPage < totalPages) {
      loadMatches(newPage);
      setActiveDropdown(null);
    }
  };

  const handleGameTypeChange = function(type) {
    setSelectedGameType(type);
  };

  const activeDropdownMatch = filteredMatches.find(function (m) {
    return m.id === activeDropdown;
  });

  // Get the selected game type label
  const selectedGameTypeLabel = GAME_TYPES.find(function(type) {
    return type.value === selectedGameType;
  })?.label || 'All';

  // Generate pagination items
  const getPaginationItems = function () {
    const items = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 0; i < totalPages; i++) {
        items.push(i);
      }
    } else {
      if (currentPage < maxVisible - 1) {
        for (let i = 0; i < maxVisible; i++) {
          items.push(i);
        }
        items.push("ellipsis");
        items.push(totalPages - 1);
      } else if (currentPage >= totalPages - maxVisible + 1) {
        items.push(0);
        items.push("ellipsis");
        for (let i = totalPages - maxVisible; i < totalPages; i++) {
          items.push(i);
        }
      } else {
        items.push(0);
        items.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          items.push(i);
        }
        items.push("ellipsis");
        items.push(totalPages - 1);
      }
    }

    return items;
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#0F2327] px-5 py-2 flex items-center justify-between">
              <span className="text-xl text-white font-normal capitalize">
                Completed Games Detail
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Filters */}
            <div className="flex items-center py-2 px-2 gap-2">
              {/* Date Range Picker */}
              <div
                className="flex items-center border border-gray-300 bg-white"
                style={{ borderRadius: 0 }}
              >
                <div className="flex-1 px-2 py-1">
                  <input
                    type="date"
                    value={startDate}
                    onChange={function(e) { setStartDate(e.target.value); }}
                    className="w-full text-sm focus:outline-none bg-transparent"
                    style={{ fontSize: "12px", width: "120px" }}
                  />
                </div>
                <div className="px-2 text-gray-400">
                  <SwapRightIcon />
                </div>
                <div className="flex-1 px-2 py-1">
                  <input
                    type="date"
                    value={endDate}
                    onChange={function(e) { setEndDate(e.target.value); }}
                    className="w-full text-sm focus:outline-none bg-transparent"
                    style={{ fontSize: "12px", width: "120px" }}
                  />
                </div>
                <div className="px-2 text-gray-400">
                  <CalendarIcon />
                </div>
              </div>

              {/* Game Type Select */}
              <div
                className="relative border border-gray-300 bg-white px-2"
                style={{ width: "300px", borderRadius: 0 }}
              >
                <div
                  onClick={function() { setShowGameTypeDropdown(!showGameTypeDropdown); }}
                  className="cursor-pointer"
                >
                  <input
                    type="text"
                    readOnly
                    value={selectedGameTypeLabel}
                    placeholder="Select Game Type"
                    className="w-full py-2 pr-6 text-sm focus:outline-none bg-transparent cursor-pointer"
                  />
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <DownIcon />
                  </span>
                </div>
                {showGameTypeDropdown && (
                  <GameTypeDropdown
                    value={selectedGameType}
                    onChange={handleGameTypeChange}
                    onClose={function() { setShowGameTypeDropdown(false); }}
                  />
                )}
              </div>

              {/* Clear Filters Button */}
              {(startDate || endDate || selectedGameType !== 'all') && (
                <button
                  onClick={function() {
                    setStartDate('');
                    setEndDate('');
                    setSelectedGameType('all');
                  }}
                  className="bg-red-500 text-white px-4 py-2 hover:bg-red-600 text-sm"
                  style={{ borderRadius: 0 }}
                >
                  Clear Filters
                </button>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium"></th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Name
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Time
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Declared Date
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Competition
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Won By
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        P/L
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMatches.length === 0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          {startDate || endDate || selectedGameType !== 'all' 
                            ? 'No matches found for the selected filters'
                            : 'No completed games found'}
                        </td>
                      </tr>
                    ) : (
                      filteredMatches.map(function (match, index) {
                        const rowBgClass = index % 2 === 0 ? "" : "bg-gray-50";
                        const pnlValue = parseFloat(match.pnl || 0);

                        return (
                          <tr
                            key={match.id}
                            className={rowBgClass + " hover:bg-gray-100"}
                          >
                            <td className="p-2 border border-gray-300">
                              <span
                                onClick={function (e) {
                                  handleDropdownClick(e, match.id);
                                }}
                                className="bg-blue-500 text-white cursor-pointer inline-flex items-center justify-center"
                                style={{ padding: "3px 3px" }}
                              >
                                <MenuDownIcon />
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.eventName}
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap">
                                {formatDisplayDate(match.createdOn)}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap">
                                {formatDisplayDate(match.createdOn)}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.matchtype || "T-20"}
                            </td>
                            <td className="p-2 border border-gray-300">
                              {match.winner || "-"}
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className={(pnlValue >= 0 ? "text-green-600" : "text-red-600") + " font-medium"}>
                                {pnlValue.toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {!loading && filteredMatches.length > 0 && (
              <div className="flex justify-end my-2 mx-3">
                <ul className="flex items-center gap-1">
                  {/* Previous Button */}
                  <li>
                    <button
                      disabled={currentPage === 0}
                      onClick={function () {
                        handlePageChange(currentPage - 1);
                      }}
                      className={
                        "px-2 py-1 border border-gray-300 " +
                        (currentPage === 0
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100")
                      }
                    >
                      <LeftIcon />
                    </button>
                  </li>

                  {/* Page Numbers */}
                  {getPaginationItems().map(function (item, idx) {
                    if (item === "ellipsis") {
                      return (
                        <li key={"ellipsis-" + idx} className="px-2 py-1">
                          <div className="flex flex-col items-center">
                            <DoubleRightIcon />
                            <span className="text-xs">•••</span>
                          </div>
                        </li>
                      );
                    }

                    const isActive = item === currentPage;
                    return (
                      <li key={item}>
                        <button
                          onClick={function () {
                            handlePageChange(item);
                          }}
                          className={
                            "min-w-[32px] px-2 py-1 border border-gray-300 " +
                            (isActive
                              ? "bg-blue-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100")
                          }
                        >
                          {item + 1}
                        </button>
                      </li>
                    );
                  })}

                  {/* Next Button */}
                  <li>
                    <button
                      disabled={currentPage >= totalPages - 1}
                      onClick={function () {
                        handlePageChange(currentPage + 1);
                      }}
                      className={
                        "px-2 py-1 border border-gray-300 " +
                        (currentPage >= totalPages - 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100")
                      }
                    >
                      <RightIcon />
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {activeDropdown && activeDropdownMatch && (
        <DropdownMenu
          match={activeDropdownMatch}
          position={dropdownPosition}
          onClose={handleCloseDropdown}
        />
      )}
    </div>
  );
};

export default CompletedGamesPage;
